import React, { useRef, useState, useEffect, useCallback } from "react";
import { Search, ChevronRight, MessageCircle, X } from "lucide-react";
import { motion } from "framer-motion";
import { ChatFramework } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useChatStore } from "@/stores/chatStore";
import { useToast } from "@/hooks/useToast";
import { cn, debounce } from "@/lib/utils";
import { apiGet } from "@/utils/apiHelper";
import { FAQ_API } from "@/config/api";
import { FAQRecommendItemData, FAQSearchItemData } from "@/types";
import { PrivacyPolicy } from "../components/PrivacyPolicy";
import {
  trackEvent,
  TRACK_EVENT,
  CLICK_ACTION,
  BUTTON_TAG,
} from "@/utils/analytics";

const LoadingDots = () => {
  const dots = 3;
  return (
    <div className="absolute flex items-center justify-center right-5 top-1/2 w-4 h-4 transform -translate-y-1/2 text-gray-400 cursor-pointer">
      <motion.div
        animate="pulse"
        transition={{ staggerChildren: -0.2, staggerDirection: -1 }}
        className="flex items-center justify-center gap-[6px]"
      >
        {Array.from({ length: dots }).map((_, index) => (
          <motion.div
            key={index}
            className="flex-shrink-0 w-[3px] h-[3px] rounded-full bg-gray-400 will-change-transform"
            variants={{
              pulse: {
                scale: [0.5, 1.5, 0.5],
                transition: {
                  duration: 1.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              },
            }}
          />
        ))}
      </motion.div>
    </div>
  );
};

export const FAQPage: React.FC = () => {
  const {
    market,
    openChat,
    closeChat,
    setEntryPath,
    setCurrentView,
    setSelectedFaq,
    hadUnreadMessage,
  } = useChatStore();
  const { toast } = useToast();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [recommendFaqs, setRecommendFaqs] = useState<FAQRecommendItemData[]>(
    [],
  );
  const [searchFaqs, setSearchFaqs] = useState<FAQSearchItemData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // NOTE: 推荐 FAQ 数据
  const recommendFAQs = async () => {
    setLoading(true);
    try {
      const response = await apiGet<{ data: { faqs: FAQRecommendItemData[] } }>(
        FAQ_API.recommend(),
        { market: market },
      );
      setRecommendFaqs(response.data.faqs);
    } catch (err) {
      console.error("Failed to recommend FAQs:", err);
      toast({
        description: "Failed to recommend FAQs, please try again later.",
      });
      // NOTE: 使用模拟数据作为后备
      const mockFAQs: FAQRecommendItemData[] = [];

      setRecommendFaqs(mockFAQs);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // NOTE: 搜索 FAQ 数据
  const searchFAQs = async (query: string) => {
    try {
      const response = await apiGet<{ data: { faqs: FAQSearchItemData[] } }>(
        FAQ_API.search(),
        { market: market, search: encodeURIComponent(query) },
      );

      setSearchFaqs(response.data.faqs);
    } catch (err) {
      console.error("Failed to search FAQs:", err);
      toast({
        description: "Failed to search FAQs, please try again later.",
      });

      // NOTE: 使用模拟数据作为后备
      const mockFAQs: FAQSearchItemData[] = [];

      setSearchFaqs(mockFAQs);
    } finally {
      setLoading(false);
    }
  };

  // NOTE: 防抖搜索 - 使用 useCallback 确保防抖函数稳定
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query) {
        searchFAQs(query);
      } else {
        recommendFAQs();
      }
    }, 300),
    [],
  );

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setLoading(true);
    debouncedSearch(query);
  };

  const renderRecommendFAQs = () => {
    return recommendFaqs.map((faq) => (
      <div
        key={faq.id}
        className={cn(
          "group flex justify-between items-center p-4 rounded-[4px] text-[13px] transition-all duration-300 cursor-pointer",
          "border border-[#919191] hover:border-[#3C101E]",
        )}
        onClick={() => {
          if (faq.type === "url") {
            window.open(faq.value, "_blank");
          } else {
            handleFAQClick({ id: faq.value, name: faq.name });
          }
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: faq.name }} />
        <div
          className={cn(
            "translate-x-0 group-hover:translate-x-2 transition-all duration-300",
          )}
        >
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    ));
  };

  const renderSearchFAQs = () => {
    return searchFaqs.map((faq) => (
      <div
        key={faq.id}
        className={cn(
          "group flex justify-between items-center p-4 rounded-[4px] text-[13px] transition-all duration-300 cursor-pointer",
          "hover:bg-gray-50",
        )}
        onClick={() => handleFAQClick(faq)}
      >
        <div
          className={cn("truncate")}
          dangerouslySetInnerHTML={{ __html: faq.name }}
        />
        <div
          className={cn(
            "translate-x-0 group-hover:translate-x-2 transition-all duration-300",
          )}
        >
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    ));
  };

  // NOTE: 处理 FAQ 点击
  const handleFAQClick = (faq: FAQSearchItemData) => {
    // 直接切换到详情页面，让 FAQDetail 组件自己获取数据
    setSelectedFaq({ id: faq.id, name: faq.name, body: "" });
    setCurrentView("faq-detail");
  };

  const handleStartChat = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "faq",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "chat_with_us",
    });
    setEntryPath("faq");
    // NOTE: 切换到 ChatBox 视图并展开聊天窗口
    setCurrentView("chatbox");
    openChat();
  };

  const handleClose = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "faq",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "close",
    });

    closeChat();
  };

  // NOTE: 初始加载热门 FAQ
  useEffect(() => {
    recommendFAQs();
  }, []);

  return (
    <ChatFramework
      header={
        <span className="pl-3 text-[18px] text-foreground">
          Hi, how can we help?
        </span>
      }
      headerContent={
        // 搜索框
        <div className="relative">
          <Search
            className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 cursor-text"
            onClick={() => searchInputRef.current?.focus()}
          />
          <Input
            ref={searchInputRef}
            type="text"
            className="py-5 pl-12 pr-[60px] h-auto border-[#919191] hover:border-[#3C101E] transition-all duration-300 cursor-text"
            placeholder="Search for answers..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {loading && <LoadingDots />}
          {!loading && searchQuery && (
            <X
              className="absolute right-5 top-1/2 w-4 h-4 transform -translate-y-1/2 text-gray-400 cursor-pointer"
              onClick={() => handleSearchChange("")}
            />
          )}
        </div>
      }
      footer={
        // 开始聊天按钮
        <div className="h-[64px]">
          <Button
            onClick={handleStartChat}
            className="w-full h-full rounded-none"
            size="sm"
          >
            {/* red unread dot */}
            <div>
              {hadUnreadMessage && (
                <div className="w-2 h-2 bg-[#C41C1C] rounded-full"></div>
              )}
            </div>
            <MessageCircle className="w-4 h-4" />
            Chat with us!
          </Button>
        </div>
      }
      footerContent={
        <div className="px-4 pb-3">
          <PrivacyPolicy />
        </div>
      }
      onClose={handleClose}
      isLoading={loading}
    >
      {/* FAQ 列表 */}
      <div className="mt-2 px-2 space-y-3">
        {!searchQuery && renderRecommendFAQs()}
        {searchQuery && searchFaqs.length === 0 && (
          <div className="text-center py-4">
            <p className="text-gray-500 text-sm">No related questions found.</p>
          </div>
        )}
        {searchQuery && searchFaqs.length > 0 && renderSearchFAQs()}
      </div>
    </ChatFramework>
  );
};
